import { FeedEvent, Lease, LeaseLifecycle, Unit } from '@urbanmix-tech/shared-js';

import { convertPseudoDateToRealDate } from '@/helpers/common';
import { getLeaseCategory } from '@/helpers/leaseHelpers';
import {
  NATIVE_LEASE_EVENT_TYPES,
  OCCUPANCY_EVENT_TYPES,
  StatusEvent,
  isStatusEvent,
} from './events';
import { deriveUnitStatus, relabel, type UnitStatusView } from './derive';

/**
 * Where the occupied/vacant call came from.
 *
 * - `events`   — a `state_tenant_moved_in`/`_out` event was recorded. Authoritative.
 * - `inferred` — no move event exists, so occupancy is read off the lease (or the
 *                legacy `currentState`). Best effort; correct for most units.
 */
export type OccupancySource = 'events' | 'inferred';

export interface ResolvedUnitStatus extends UnitStatusView {
  occupancySource: OccupancySource;
  /** True when the unit has at least one `state_*` event of any kind. */
  hasStatusEvents: boolean;
  /**
   * When the executed renewal takes effect (ISO date), if we can establish it.
   * Lets the UI distinguish "signed, starts next week" from "renewed months ago".
   */
  renewalEffectiveDate?: string;
}

/**
 * Infer occupancy for a unit with no recorded move events.
 *
 * Order matters: an active lease is the strongest legacy signal (it's what the
 * occupancy header has always counted). `currentState` is the fallback below it
 * — it covers holdover tenants whose lease has lapsed but who never moved out,
 * and it's what the rent-roll importers stamp (`DONE` = occupied, `LEASE` =
 * vacant/leasing), as does the workflow server on move-in/move-out.
 */
function inferOccupancy(unit: Unit | undefined, leases: Lease[]): 'occupied' | 'vacant' {
  if (leases.some((lease) => getLeaseCategory(lease) === 'active')) {
    return 'occupied';
  }

  const currentState = unit?.currentState;
  const states = Array.isArray(currentState) ? currentState : [currentState];
  if (states.includes(LeaseLifecycle.DONE)) {
    return 'occupied';
  }

  return 'vacant';
}

const OCCUPANCY_EVENTS = new Set<string>(OCCUPANCY_EVENT_TYPES);
const NATIVE_LEASE_EVENTS = new Set<string>(NATIVE_LEASE_EVENT_TYPES);

/**
 * Discard native lease events whose process no longer exists.
 *
 * Native events carry no lease type, so the fold classifies them via
 * `processTypeById` and — per upstream — defaults an unknown process id to an
 * *incoming new lease*. On real data a chunk of the feed points at processes
 * that were deleted, leaving orphaned events behind; defaulting those to
 * "incoming" invents a tenant that doesn't exist. Since the process is gone we
 * have no way to classify them, and they're old enough to be irrelevant, so the
 * honest move is to drop them rather than guess.
 *
 * Two deliberate exclusions:
 * - Our own `state_*` events are never dropped. They carry explicit `leaseType`
 *   and stand on their own, whatever happened to the process.
 * - An event with no `processId` at all isn't an orphan — nothing claims it had
 *   a process — so upstream's default applies.
 *
 * No map (or an empty one) means "we can't tell", not "everything is orphaned":
 * `state.project.processes` is empty on first render, and dropping every native
 * event there would blank the status and then flicker back. So this is a no-op
 * until the map has content.
 */
function dropOrphanedLeaseEvents(
  events: FeedEvent[],
  processTypeById?: Record<string, string>
): FeedEvent[] {
  if (!processTypeById || Object.keys(processTypeById).length === 0) return events;

  return events.filter((e) => {
    if (!e || !NATIVE_LEASE_EVENTS.has(e.eventType)) return true;
    const procId = e.processId ?? (e.eventData as { processId?: string } | undefined)?.processId;
    if (!procId) return true;
    return processTypeById[procId] !== undefined;
  });
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How long each kind of overlay stays worth showing.
 *
 * Every overlay describes a transition *in flight*. Once its date has passed —
 * or it's simply gone quiet for long enough — it stops being news and showing it
 * is just noise on an otherwise ordinary occupied unit. The fold itself has no
 * concept of "now", so this decay lives here, in the presentation layer.
 */
export interface FreshnessPolicy {
  /** A renewal stops being news once its lease has been in effect this long. */
  renewedDays: number;
  /** A scheduled move-in/move-out is stale this long past its scheduled date. */
  scheduledDays: number;
  /** An undated signal (listing, negotiation, signing) goes quiet after this. */
  signalDays: number;
}

export const DEFAULT_FRESHNESS: FreshnessPolicy = {
  renewedDays: 30,
  scheduledDays: 30,
  signalDays: 90,
};

function toTime(value: unknown): number | null {
  const d = convertPseudoDateToRealDate(value as Date | null | undefined);
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.getTime();
}

/** The most recent event of a given type, or undefined. */
function latestOf(events: FeedEvent[], type: string): FeedEvent | undefined {
  let latest: FeedEvent | undefined;
  let latestAt = -Infinity;
  events.forEach((e) => {
    if (!e || e.eventType !== type) return;
    const t = toTime(e.eventDate) ?? -Infinity;
    if (t >= latestAt) {
      latest = e;
      latestAt = t;
    }
  });
  return latest;
}

/** Days elapsed since `time`; negative when it's still in the future. */
function daysSince(time: number | null, now: number): number | null {
  if (time === null) return null;
  return (now - time) / DAY_MS;
}

/**
 * When the lease a signing produced actually begins.
 *
 * `state_lease_signed` is supposed to carry the lease start in `eventData.date`,
 * but in practice it's null on every event we have. Falling back to the
 * *signature* date is not a workable substitute: leases — renewals especially —
 * are executed weeks or months ahead of the term, so a renewal signed 60 days
 * ago may not start for another week. Judging staleness by signature age retires
 * renewals that haven't even begun.
 *
 * So look the term up instead: the earliest lease on this unit that starts on or
 * after the signing. Drafts count — a countersigned renewal often sits as a draft
 * lease until its term opens.
 */
function leaseStartOnOrAfter(leases: Lease[], from: number | null): number | null {
  if (from === null) return null;
  let best: number | null = null;
  (leases ?? []).forEach((lease) => {
    if ((lease as { status?: string }).status === 'canceled') return;
    const start = toTime(lease.leaseStartDate);
    if (start === null || start < from) return;
    if (best === null || start < best) best = start;
  });
  return best;
}

/**
 * Drop overlays that have gone stale, leaving the plain occupancy behind.
 *
 * Worked example (the common one): a tenant renews, the new lease starts, and
 * two months later the unit is still flagged "Renewed". Nothing about that is
 * actionable — the unit is just occupied. So once the renewed lease has been in
 * effect longer than `renewedDays`, the chip goes away.
 *
 * The same reasoning retires a move-in that was scheduled for a date well in the
 * past (the tenant moved in; nobody recorded the event), a move-out likewise,
 * and listings/negotiations that have simply gone quiet.
 */
function applyFreshness(
  view: UnitStatusView,
  events: FeedEvent[],
  leases: Lease[],
  now: number,
  policy: FreshnessPolicy
): { view: UnitStatusView; renewalEffectiveDate?: string } {
  let current = view.current;
  let incoming = view.incoming;
  let listing = view.listing;

  const signed = latestOf(events, StatusEvent.LeaseSigned);
  const signedAt = toTime(signed?.eventDate);
  // The term this signing opened: the event's own date if it has one, otherwise
  // the matching lease. Never the signature date — see leaseStartOnOrAfter.
  const termStart = toTime(signed?.eventData?.date) ?? leaseStartOnOrAfter(leases, signedAt);
  const renewalEffectiveDate =
    current?.renewed && termStart !== null ? new Date(termStart).toISOString() : undefined;

  if (current?.renewed) {
    const age = daysSince(termStart, now);
    if (age !== null) {
      // A renewal that hasn't started yet is still news, so only elapsed time
      // past the threshold retires it.
      if (age > policy.renewedDays) current = { ...current, renewed: undefined };
    } else {
      // No term to measure against at all — fall back to a deliberately generous
      // window on the signature so a chip can't linger forever on bad data.
      const signatureAge = daysSince(signedAt, now);
      if (signatureAge !== null && signatureAge > policy.signalDays) {
        current = { ...current, renewed: undefined };
      }
    }
  }

  if (current?.renewal) {
    const type =
      current.renewal === 'negotiating'
        ? StatusEvent.RenewalNegotiating
        : StatusEvent.RenewalTermsAgreed;
    const age = daysSince(toTime(latestOf(events, type)?.eventDate), now);
    if (age !== null && age > policy.signalDays) {
      current = { ...current, renewal: undefined };
    }
  }

  if (current?.moveOut) {
    const scheduled = toTime(current.moveOut.date);
    const age = daysSince(scheduled ?? toTime(latestOf(events, StatusEvent.MoveoutScheduled)?.eventDate), now);
    const limit = scheduled !== null ? policy.scheduledDays : policy.signalDays;
    if (age !== null && age > limit) {
      current = { ...current, moveOut: undefined };
    }
  }

  if (incoming) {
    if (incoming.lease === 'executed' || incoming.moveIn) {
      // Prefer the scheduled move-in, then the term the signing opened. Once
      // either is well past, this tenant isn't "incoming" — they're the occupant,
      // and no `state_tenant_moved_in` was ever recorded to close the loop.
      const dated = toTime(incoming.moveIn?.date) ?? termStart;
      if (dated !== null) {
        const age = daysSince(dated, now);
        if (age !== null && age > policy.scheduledDays) incoming = null;
      } else {
        const signatureAge = daysSince(signedAt, now);
        if (signatureAge !== null && signatureAge > policy.signalDays) incoming = null;
      }
    } else {
      // Drafted or sent but never executed. There's no term to measure against —
      // a lease that's sat unsigned this long has stalled or been abandoned, so
      // judge it on the age of its own latest pipeline event.
      const latestPipelineAt = Math.max(
        ...NATIVE_LEASE_EVENT_TYPES.map((t) => toTime(latestOf(events, t)?.eventDate) ?? -Infinity)
      );
      const age = daysSince(Number.isFinite(latestPipelineAt) ? latestPipelineAt : null, now);
      if (age !== null && age > policy.signalDays) incoming = null;
    }
  }

  if (listing) {
    const type =
      listing === 'showing' ? StatusEvent.UnitShown : StatusEvent.ListingPublished;
    const age = daysSince(toTime(latestOf(events, type)?.eventDate), now);
    if (age !== null && age > policy.signalDays) listing = null;
  }

  // Collapse a current-tenant record that no longer says anything.
  if (current && !current.renewed && !current.renewal && !current.moveOut && !current.tenantName) {
    current = null;
  }

  return {
    view: relabel({ ...view, current, incoming, listing }),
    renewalEffectiveDate: current?.renewed ? renewalEffectiveDate : undefined,
  };
}

/**
 * Fold a unit's status events into a view, sourcing occupancy honestly.
 *
 * `deriveUnitStatus()` defaults occupancy to "occupied" and only flips it on a
 * move event. That baseline is wrong for every building predating the
 * move-out/listing/move-in workflows — with no events at all, every unit would
 * read "Occupied", including the empty ones. So:
 *
 * - **Overlays** (renewal stage, notice, listing, incoming tenant) always come
 *   straight from the fold. There's no legacy equivalent, so there's nothing to
 *   reconcile — absent events just mean no overlay.
 * - **Occupancy** comes from the fold when a move event exists, and from the
 *   lease otherwise.
 *
 * Inferring "vacant" also clears `current`, exactly as a recorded
 * `state_tenant_moved_out` would — there is no sitting tenant to describe.
 * `incoming` and `listing` survive, since those describe the re-lease in flight,
 * which is the whole point of looking at a vacant unit.
 *
 * Finally, stale overlays are retired (see `applyFreshness`) so a unit that
 * renewed months ago reads as plain "Occupied" rather than carrying a "Renewed"
 * chip forever.
 *
 * @param events Status events for this unit (extra feed noise is ignored).
 * @param leases Leases for this unit.
 * @param opts   `now` and `freshness` are injectable for testing.
 */
export function resolveUnitStatus(
  unit: Unit | undefined,
  events: FeedEvent[],
  leases: Lease[] = [],
  opts: {
    now?: Date;
    freshness?: FreshnessPolicy;
    /**
     * processId → process type, so native lease events can be told apart as a
     * renewal vs a new lease. Without it every renewal reads as an incoming
     * tenant — see `deriveUnitStatus`.
     */
    processTypeById?: Record<string, string>;
  } = {}
): ResolvedUnitStatus {
  const now = (opts.now ?? new Date()).getTime();
  const policy = opts.freshness ?? DEFAULT_FRESHNESS;

  // Filter once, up front, so the fold and the freshness pass see the same
  // events — otherwise freshness could decay an overlay the fold never built.
  const statusEvents = dropOrphanedLeaseEvents(events ?? [], opts.processTypeById);

  const { view: derived, renewalEffectiveDate } = applyFreshness(
    deriveUnitStatus(statusEvents, { processTypeById: opts.processTypeById }),
    statusEvents,
    leases,
    now,
    policy
  );

  const hasStatusEvents = statusEvents.some((e) => e && isStatusEvent(e.eventType));
  const hasOccupancyEvent = statusEvents.some((e) => e && OCCUPANCY_EVENTS.has(e.eventType));

  if (hasOccupancyEvent) {
    return { ...derived, occupancySource: 'events', hasStatusEvents, renewalEffectiveDate };
  }

  const occupancy = inferOccupancy(unit, leases);
  if (occupancy === derived.occupancy) {
    return { ...derived, occupancySource: 'inferred', hasStatusEvents, renewalEffectiveDate };
  }

  const vacant = occupancy === 'vacant';
  return {
    ...relabel({ ...derived, occupancy, current: vacant ? null : derived.current }),
    occupancySource: 'inferred',
    hasStatusEvents,
    renewalEffectiveDate: vacant ? undefined : renewalEffectiveDate,
  };
}

/**
 * The short chip text for a status — the headline, without the overlay detail
 * that `label` spells out.
 */
export function statusHeadline(status: UnitStatusView): string {
  return status.occupancy === 'occupied' ? 'Occupied' : 'Vacant';
}

/**
 * A single grouping key for filtering/visualizing units by status.
 *
 * Deliberately coarse — these become distinct colors on the 3D model and
 * distinct filter options, so the set has to stay small and stable.
 */
export type UnitStatusGroup =
  | 'Occupied'
  | 'Renewal in progress'
  | 'Renewed'
  | 'Move out scheduled'
  | 'Vacant'
  | 'Listed'
  | 'New lease in progress'
  | 'Leased — move-in pending';

export function unitStatusGroup(status: UnitStatusView): UnitStatusGroup {
  const incoming = status.incoming;
  if (incoming?.moveIn || incoming?.lease === 'executed') {
    return 'Leased — move-in pending';
  }
  // A lease that's drafted or out for signature isn't committed yet, so it gets
  // its own bucket rather than being lumped in with executed ones.
  if (incoming?.lease === 'created' || incoming?.lease === 'sent') {
    return 'New lease in progress';
  }
  if (status.occupancy === 'vacant') {
    return status.listing ? 'Listed' : 'Vacant';
  }
  if (status.current?.moveOut) return 'Move out scheduled';
  if (status.current?.renewed) return 'Renewed';
  if (status.current?.renewal) return 'Renewal in progress';
  return 'Occupied';
}

/** Stable display order for the groups (filters, legends, summary rows). */
export const UNIT_STATUS_GROUPS: UnitStatusGroup[] = [
  'Occupied',
  'Renewal in progress',
  'Renewed',
  'Move out scheduled',
  'New lease in progress',
  'Leased — move-in pending',
  'Listed',
  'Vacant',
];
